import React, { useRef, useState, useEffect } from "react";
import * as styles from "./ScrollSpyMenu.module.scss";
import { useIntl } from "gatsby-plugin-intl";

const discoveryData = [
	{ id: "01", title: "full-cycle.discovery.title1", content: "full-cycle.discovery.text1" },
	{ id: "02", title: "full-cycle.discovery.title2", content: "full-cycle.discovery.text2" },
	{ id: "03", title: "full-cycle.discovery.title3", content: "full-cycle.discovery.text3" },
	{ id: "04", title: "full-cycle.discovery.title4", content: "full-cycle.discovery.text4" },
	{ id: "05", title: "full-cycle.discovery.title5", content: "full-cycle.discovery.text6" },
];

const documentsList = [
	{ title: "full-cycle.documents.title1" },
	{ title: "full-cycle.documents.title2" },
	{ title: "full-cycle.documents.title3" },
	{ title: "full-cycle.documents.title4" },
	{ title: "full-cycle.documents.title5" },
	{ title: "full-cycle.documents.title6" },
];

const deployList = [
	{ img: "/images/full-cycle-software/deploy-production-icon.svg", title: "full-cycle.deploy.title1" },
	{ img: "/images/full-cycle-software/deploy-ug-icon.svg", title: "full-cycle.deploy.title2" },
	{ img: "/images/full-cycle-software/deploy-ut-icon.svg", title: "full-cycle.deploy.title3" },
	{ img: "/images/full-cycle-software/deploy-uat-icon.svg", title: "full-cycle.deploy.title4" },
];

const supportList = [
	{ number: "01", title: "full-cycle.support.title1" },
	{ number: "02", title: "full-cycle.support.title2" },
	{ number: "03", title: "full-cycle.support.title3" },
];

const Discovery = () => {
	const intl = useIntl();
	return (
		<ul className={styles.discoveryList}>
			{discoveryData.map((item, index) => (
				<li className={styles.discoveryItem} key={index}>
					<div className={styles.discoveryNumber}>{item.id}</div>
					<div className={styles.discoveryContent}>
						<h3>{intl.formatMessage({ id: item.title })}</h3>
						<p>{intl.formatMessage({ id: item.content })}</p>
					</div>
				</li>
			))}
		</ul>
	);
};

const Documents = () => {
	const intl = useIntl();
	return (
		<>
			<p className={styles.documentsText}>As a result, you get:</p>
			<ul className={styles.documentsList}>
				{documentsList.map((item, index) => (
					<li className={styles.documentsItem} key={index}>
						<div className={styles.documentsLine} />
						<h3>{intl.formatMessage({ id: item.title })}</h3>
					</li>
				))}
			</ul>
		</>
	);
};

const Development = () => {
	const intl = useIntl();
	return (
		<div className={styles.development}>
			<p className={styles.developmentTitle}>
				{intl.formatMessage({ id: "full-cycle.development.subtitle" })}
			</p>
			<div className={styles.developmentStages}>
				<p>{intl.formatMessage({ id: "full-cycle.development.text" })}</p>
				<ul>
					<li>
						PoC <span>({intl.formatMessage({ id: "full-cycle.development.title1" })})</span>
					</li>
					<li>
						MVP <span>({intl.formatMessage({ id: "full-cycle.development.title2" })})</span>
					</li>
					<li>{intl.formatMessage({ id: "full-cycle.development.title3" })}</li>
				</ul>
			</div>
			<div className={styles.developmentImg}>
				<img
					className={styles.developmentImgDesktop}
					src="/images/full-cycle-software/steps.svg"
					alt="Steps"
				/>
				<img
					className={styles.developmentImgMobile}
					src="/images/full-cycle-software/steps-mobile.svg"
					alt="Steps"
				/>
			</div>
			<div className={styles.developmentText}>
				<span />
				<p>{intl.formatMessage({ id: "full-cycle.development.bottom-text" })}</p>
			</div>
		</div>
	);
};

const Deploy = () => {
	const intl = useIntl();
	return (
		<div className={styles.deploy}>
			<p className={styles.deployTitle}>
				{intl.formatMessage({ id: "full-cycle.deploy.subtitle" })}
			</p>
			<ul className={styles.deployList}>
				{deployList.map((item, index) => (
					<li key={index} className={styles.deployItem}>
						<img src={item.img} alt={item.title} />
						<h3>{intl.formatMessage({ id: item.title })}</h3>
					</li>
				))}
			</ul>
			<div className={styles.deployResult}>
				<h3>{intl.formatMessage({ id: "full-cycle.deploy.title5" })}</h3>
				<p>{intl.formatMessage({ id: "full-cycle.deploy.text" })}</p>
			</div>
		</div>
	);
};

const Support = () => {
	const intl = useIntl();
	return (
		<div className={styles.support}>
			<div className={styles.supportText}>
				<p>{intl.formatMessage({ id: "full-cycle.support.subtitle" })}</p>
				<p>{intl.formatMessage({ id: "full-cycle.support.subtitle2" })}</p>
			</div>
			<ul className={styles.supportList}>
				{supportList.map((item, index) => (
					<li key={index} className={styles.supportItem}>
						<p className={styles.supportNumber}>{item.number}</p>
						<h3 className={styles.supportTitle}>{intl.formatMessage({ id: item.title })}</h3>
					</li>
				))}
			</ul>
		</div>
	);
};

const sectionsData = [
	{ id: "1", title: "full-cycle.discovery.link-name", content: <Discovery /> },
	{ id: "2", title: "full-cycle.documents.link-name", content: <Documents /> },
	{ id: "3", title: "full-cycle.development.link-name", content: <Development /> },
	{ id: "4", title: "full-cycle.deploy.link-name", content: <Deploy /> },
	{ id: "5", title: "full-cycle.support.link-name", content: <Support /> },
];

const ScrollSpyMenu: React.FC = () => {
	const intl = useIntl();
	const [activeSection, setActiveSection] = useState<string>(sectionsData[0].id);
	const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
	const OFFSET = 80;

	useEffect(() => {
		let ticking = false;

		const handleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					let currentSectionId = activeSection;

					for (let i = 0; i < sectionsData.length; i++) {
						const section = sectionRefs.current[i];
						if (section) {
							const rect = section.getBoundingClientRect();
							if (rect.top - OFFSET <= 0 && rect.bottom - OFFSET > 0) {
								currentSectionId = sectionsData[i].id;
								break;
							}
						}
					}

					if (currentSectionId !== activeSection) {
						setActiveSection(currentSectionId);
					}

					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [activeSection]);

	const scrollToSection = (index: number) => {
		const section = sectionRefs.current[index];
		if (section) {
			const y = section.getBoundingClientRect().top + window.pageYOffset - OFFSET + 1;
			window.scrollTo({ top: y, behavior: "smooth" });
		}
	};

	return (
		<div className={styles.wrap}>
			<nav className={styles.navigation}>
				{sectionsData.map((section, index) => (
					<button
						key={section.id}
						onClick={() => scrollToSection(index)}
						className={`${styles.navigationItem} ${activeSection === section.id ? styles.navigationActive : ""
							}`}
					>
						{intl.formatMessage({ id: section.title })}
					</button>
				))}
			</nav>

			<div className={styles.content}>
				{sectionsData.map((section, index) => (
					<div
						className={styles.contentItem}
						key={section.id}
						ref={(el) => (sectionRefs.current[index] = el)}
						id={section.id}
					>
						{section.content}
					</div>
				))}
			</div>
		</div>
	);
};

export default ScrollSpyMenu;
